<template>
  <div id="ingredients" class="md:h-full bg-primary-red px-4 md:px-8 lg:p-16 py-8">
    <!-- Title -->
    <h2 class="ltr:font-ltr-heading ltr:tracking-wider text-3xl mb-8 text-white">
      {{ title }}
    </h2>

    <!-- Portion selector -->
    <div
      v-if="portions"
      class="flex bg-white rounded-lg mb-6"
    >
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 ltr:border-r-2 rtl:border-l-2 border-beige"
        data-gtm-trigger="click"
        @click="performStep('down')"
      >
        <IconMinus
          class="icon m-auto"
          width="16px"
        />
      </button>
      <div class="flex-1 flex items-center h-12">
        <span class="text-xs font-light mx-auto">{{ portions }} {{ amountText }}</span>
      </div>
      <button
        v-if="ingredients.amountIsScalable"
        class="w-12 h-12 ltr:border-l-2 rtl:border-r-2 border-beige"
        data-gtm-trigger="click"
        @click="performStep('up')"
      >
        <IconPlus
          class="icon m-auto"
          width="16px"
        />
      </button>
    </div>

    <!-- Ingredient list -->
    <div
      v-for="ingredientGroup in scaledIngredientsGroups"
      :key="ingredientGroup.index"
    >
      <h3 v-if="ingredientGroup.name" class="text-2xl mb-4 text-white">
        {{ ingredientGroup.name }}
      </h3>

      <ul class="recipe-ingredients text-xs font-light bg-white rounded-lg mb-10">
        <li v-for="ingredient in ingredientGroup.items"
            :key="ingredient.id"
            class="flex items-baseline justify-between p-4 border-t-2 border-beige first:border-t-0"
        >
          <input
            :id="ingredientGroup.index + ingredient.name.value.ingredientName"
            :name="ingredientGroup.name"
            class="ltr:mr-0 rtl:ml-0 order-1 rtl:order-first"
            type="checkbox"
            @change="trackIngredientTick(ingredient.name.value.ingredientName, $event.target.checked)"
          >
          <label
            :for="ingredientGroup.index + ingredient.name.value.ingredientName"
            class="flex-1 order-first flex items-center"
          >
            <div class="flex w-full items-center">
              <a
                v-if="ingredient.product"
                :href="ingredient.product.url"
                class="flex w-full items-center underline hover:no-underline ltr:pr-2 rtl:pl-2"
              >
                <span class="ltr:mr-2 rtl:ml-2">
                  {{ ingredient.name.value.ingredientUnitName }}{{ ingredient.secondaryUnit.value ? ` (${ingredient.secondaryUnit.value})` : '' }} {{ capitilize(ingredient.name.value.ingredientName) }}
                </span>
                <Picture
                  v-bind="ingredient.product.image"
                  width="48"
                  height="48"
                  lazy
                  img-class="m-auto"
                  class="w-16 h-16 content-center flex-shrink-0  ltr:ml-auto rtl:mr-auto"
                />
              </a>
              <span
                v-else
                class="ltr:pr-2 rtl:pl-2"
              >
                {{ ingredient.name.value.ingredientUnitName }}{{ ingredient.secondaryUnit.value ? ` (${ingredient.secondaryUnit.value})` : '' }} {{ capitilize(ingredient.name.value.ingredientName) }}
              </span>
            </div>
          </label>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import IconMinus from '@/assets/icons/minus.svg'
import IconPlus from '@/assets/icons/plus.svg'
import ingredientScaleHelper from '@/compositions/recipes/RecipeIngrediensScaleHelper'
import Picture from '@/components/Picture.vue'
import { computed } from 'vue'

export default {
  components: {
    IconMinus,
    IconPlus,
    Picture
  },
  props: {
    ingredients: {
      type: Object,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    language: {
      type: String,
      default: ''
    },
    portionSingularText: {
      type: String,
      default: 'portion'
    },
    portionPluralText: {
      type: String,
      default: 'portions'
    }
  },
  setup(props) {
    const { setPortions, state } = ingredientScaleHelper(props.ingredients, 'en', props.portionSingularText, props.portionPluralText)

    const minValue = 1
    const maxValue = state.basePortions * 10
    const stepValue = 1

    function performStep(action) {
      let newValue
      if (action === 'up') {
        newValue = Math.floor((state.portions + stepValue) / stepValue) * stepValue
      } else {
        newValue = Math.ceil((state.portions - stepValue) / stepValue) * stepValue
      }

      if (action === 'up' && newValue <= maxValue) {
        setPortions(Math.floor((state.portions + stepValue) / stepValue) * stepValue)
      } else if (action === 'down' && newValue >= minValue) {
        setPortions(Math.ceil((state.portions - stepValue) / stepValue) * stepValue)
      }
    }

    function capitilize(name) {
      return name.charAt(0).toUpperCase() + name.slice(1)
    }

    function trackIngredientTick(name, checked) {
      if (checked) {
        window.dataLayer.push({
          event: 'checkIngredient',
          ingredientName: name
        })
      }
    }

    return {
      minValue,
      maxValue,
      stepValue,
      portions: computed(() => state.portions),

      amountText: computed(() => state.amountText),
      performStep,
      scaledIngredientsGroups: computed(() => state.ingredientGroups),

      capitilize,
      trackIngredientTick
    }
  }
}
</script>
<style>
  .recipe-ingredients input[type=checkbox] {
    width: 20px;
    height: 20px;
    margin: 0;
  }
</style>
